package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.LinkButton
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideArrowLeft
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlay
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSettings
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTrash
import co.maytes.standupbot.dashboard.styles.Palette
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private val actionStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Active,
    GalleryState.Disabled,
    GalleryState.Loading,
)
private val linkStates = listOf(
    GalleryState.Rest,
    GalleryState.Hover,
    GalleryState.Focus,
    GalleryState.Active,
    GalleryState.Disabled,
)

private const val LOCK_DEMO_ID = "ds-loading-lock-demo"
private const val LOCK_SETTLE_MS = 80L
private const val LOCK_HOLD_MS = 1_500L

private fun iconPx(size: ButtonSize): Int = if (size == ButtonSize.Sm) 14 else 16

@Composable
fun ActionsSection() {
    GallerySection(
        id = "actions",
        title = "Actions",
        description = "Button, IconButton and LinkButton. Hover changes colour and border only, active is the shared " +
            "press (0.98 for Button, 0.96 for IconButton; 100 ms down, 160 ms up), focus-visible is the shared " +
            "instant ring, disabled is aria-disabled with no hover, loading swaps the leading icon for a 14 px " +
            "spinner and locks the width.",
    ) {
        ButtonMatrix()
        ButtonSlotsMatrix()
        LoadingLockDemo()
        IconButtonMatrix()
        TooltipWarmDemo()
        LinkButtonMatrix()
        GalleryGrid(minColumn = 360) {
            KeyboardChecklist(
                "Button",
                listOf(
                    keys("Move focus to the button", "Tab"),
                    keys("Activate", "Enter"),
                    keys("Activate", "Space"),
                    keys("Disabled and loading buttons stay focusable but ignore activation", "Enter", "Space"),
                ),
            )
            KeyboardChecklist(
                "IconButton",
                listOf(
                    keys("Move focus; the tooltip opens after 400 ms, or at once within 1 s of the previous one", "Tab"),
                    keys("Activate", "Enter"),
                    keys("Activate", "Space"),
                    keys("Dismiss the tooltip without leaving the button", "Esc"),
                ),
            )
            KeyboardChecklist(
                "LinkButton",
                listOf(
                    keys("Move focus to the link", "Tab"),
                    keys("Follow the link (a real anchor)", "Enter"),
                    keys("External links open in a new tab with rel noopener noreferrer", "Enter"),
                ),
            )
        }
    }
}

@Composable
private fun ButtonMatrix() = GalleryMatrix(
    component = "Button: variant x size x state",
    variants = ButtonVariant.entries.map { it.name },
    sizes = ButtonSize.entries.map { it.name },
    states = actionStates,
    columnMin = 140,
) { variant, size, state ->
    Button(
        label = variant,
        variant = ButtonVariant.valueOf(variant),
        size = ButtonSize.valueOf(size),
        enabled = state != GalleryState.Disabled,
        loading = state == GalleryState.Loading,
        onClick = {},
    )
}

@Composable
private fun ButtonSlotsMatrix() = GalleryMatrix(
    component = "Button: icon slots",
    variants = listOf("Leading icon", "Trailing icon", "Both icons"),
    sizes = listOf(ButtonSize.Sm.name, ButtonSize.Md.name, ButtonSize.Lg.name),
    states = listOf(GalleryState.Rest, GalleryState.Hover, GalleryState.Focus, GalleryState.Loading),
    note = "Loading replaces the leading icon with the spinner; a trailing icon stays.",
    columnMin = 168,
) { variant, size, state ->
    val buttonSize = ButtonSize.valueOf(size)
    val px = iconPx(buttonSize)
    Button(
        label = "Run now",
        variant = ButtonVariant.Secondary,
        size = buttonSize,
        loading = state == GalleryState.Loading,
        leadingIcon = if (variant != "Trailing icon") ({ LucidePlay(size = px) }) else null,
        trailingIcon = if (variant != "Leading icon") ({ LucideChevronDown(size = px) }) else null,
        onClick = {},
    )
}

@Composable
private fun LoadingLockDemo() {
    var loading by remember { mutableStateOf(false) }
    var report by remember { mutableStateOf("Click the button to measure it before, during and after loading.") }
    val scope = rememberCoroutineScope()

    fun measure(): Int =
        (document.querySelector("#$LOCK_DEMO_ID button") as? HTMLElement)?.offsetWidth ?: 0

    GalleryPanel {
        GalleryHeading("Loading keeps the width")
        GalleryNote(
            "The button locks to the width it had before loading started, so surrounding layout does not shift. " +
                "Clicks are ignored while aria-busy is true.",
        )
        GalleryRow {
            Div(attrs = { attr("id", LOCK_DEMO_ID) }) {
                Button(
                    label = "Save changes",
                    variant = ButtonVariant.Primary,
                    loading = loading,
                    onClick = {
                        val before = measure()
                        loading = true
                        scope.launch {
                            delay(LOCK_SETTLE_MS)
                            val during = measure()
                            delay(LOCK_HOLD_MS)
                            loading = false
                            delay(LOCK_SETTLE_MS)
                            report = "before ${before}px, during ${during}px, after ${measure()}px"
                        }
                    },
                )
            }
            Button(label = "Neighbour", variant = ButtonVariant.Secondary, onClick = {})
            Button(label = "Full width", variant = ButtonVariant.Secondary, fullWidth = true, onClick = {})
        }
        GalleryMono(report, Palette.textPrimary)
    }
}

@Composable
private fun IconButtonMatrix() = GalleryMatrix(
    component = "IconButton: size x state",
    variants = listOf("Settings", "Delete"),
    sizes = ControlSize.entries.map { it.name },
    states = actionStates,
    columnMin = 120,
) { variant, size, state ->
    val controlSize = ControlSize.valueOf(size)
    IconButton(
        ariaLabel = variant,
        size = controlSize,
        enabled = state != GalleryState.Disabled,
        loading = state == GalleryState.Loading,
        onClick = {},
    ) {
        if (variant == "Settings") LucideSettings(size = 16) else LucideTrash(size = 16)
    }
}

@Composable
private fun TooltipWarmDemo() {
    GalleryPanel {
        GalleryHeading("IconButton tooltip warm-up")
        GalleryNote(
            "Hover or Tab onto the first button: its label shows after 400 ms. Move to a neighbour within a " +
                "second of the previous tooltip closing and the next one shows at once.",
        )
        GalleryRow(gap = 8) {
            IconButton(ariaLabel = "Settings", onClick = {}) { LucideSettings(size = 16) }
            IconButton(ariaLabel = "Delete", onClick = {}) { LucideTrash(size = 16) }
            IconButton(ariaLabel = "Back", onClick = {}) { LucideArrowLeft(size = 16) }
            IconButton(ariaLabel = "Run now", onClick = {}) { LucidePlay(size = 16) }
        }
    }
}

@Composable
private fun LinkButtonMatrix() = GalleryMatrix(
    component = "LinkButton: variant x state",
    variants = ButtonVariant.entries.map { it.name },
    sizes = listOf("Internal", "External"),
    states = linkStates,
    columnMin = 148,
) { variant, kind, state ->
    LinkButton(
        label = if (kind == "External") "Open docs" else "Upgrade",
        href = if (kind == "External") "https://example.com" else "#actions",
        variant = ButtonVariant.valueOf(variant),
        external = kind == "External",
        enabled = state != GalleryState.Disabled,
    )
}
